import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import {
  CTAButton,
  H2,
  HeroH1,
  InlineLink,
  P,
  SeoPageShell,
} from "./seo/SeoPageLayout";

const WhyWalkingOnEggshells = () => {
  return (
    <SeoPageShell>
      <SEO
        title="Why Do I Feel Like I'm Walking on Eggshells? (And Why It's Not You)"
        description="The constant dread, the self-censoring, the relief that never lasts — why you feel like you're always walking on eggshells, and why it's a stress response, not a personality flaw."
        path="/why-do-i-feel-like-im-walking-on-eggshells"
      />

      <HeroH1>Why You Feel Like You're Always Walking on Eggshells</HeroH1>

      <P>
        You check their mood before you say anything. You rehearse a simple sentence three times
        so it can't be taken the wrong way. When they're in a good mood, you feel relief — and
        then something close to dread, because you know it won't last. If you're exhausted from
        managing someone else's reactions, that exhaustion is telling you something true.
      </P>
      <P>
        If the cost has been building for a while —{" "}
        <InlineLink to="/why-do-i-feel-exhausted-after-conversations">
          why these conversations leave you so exhausted →
        </InlineLink>
      </P>

      <H2>It's not a personality trait. It's a stress response.</H2>
      <P>
        Walking on eggshells isn't who you are — it's what you learned. Somewhere along the way,
        ordinary moments started having consequences: a normal question got treated as an attack,
        a small disagreement turned into hours of fallout. So your mind did the sensible thing.
        It started scanning for danger before danger arrived. That constant low-level alertness
        has a name — hypervigilance — and it's a survival response, not a flaw in you.
      </P>
      <P>
        If you want to see{" "}
        <InlineLink to="/manipulative-text-message-analysis">
          what this stress response actually is →
        </InlineLink>{" "}
        laid out across the patterns that produce it, that's where the mechanics live.
      </P>

      <H2>Why you censor yourself before you even speak</H2>
      <P>
        You catch yourself editing. Softening the request. Deciding it's not worth bringing up.
        From the outside it can look like you've gone quiet or passive — but inside, you're doing
        constant, exhausting work: predicting their reaction and adjusting yourself to prevent
        it. That's not poor communication. It's what happens when speaking plainly has been
        punished enough times that silence starts to feel safer.
      </P>

      <H2>Why their good moods don't bring relief</H2>
      <P>
        You'd think the calm stretches would feel like rest. Instead they often feel like
        waiting. When the peace depends entirely on their mood — and the mood can turn for
        reasons you can never quite predict — calm stops being calm. It becomes the quiet before
        you find out what version of them you're getting today. The unpredictability is the
        thing that wears you down, more than any single bad moment.
      </P>

      <CTAButton />

      <H2>Why it's so hard to point to what's wrong</H2>
      <P>
        This is the part that makes you doubt yourself. Nothing on the list looks that bad
        written down — a sharp tone here, a silent treatment there, a comment you can't quite
        call cruel. So you wonder if you're being dramatic. But walking on eggshells isn't
        caused by any one event. It's caused by the pattern — the accumulated training that
        taught you to brace. The reason you can't point to it is that it was never one thing.
        It was the rhythm of many small things.
      </P>

      <H2>Seeing the pattern instead of carrying it</H2>
      <P>
        When the moments are scattered across weeks, they feel like your oversensitivity. Laid
        out in sequence — the tone, the withdrawal, the way a normal ask became a problem — the
        pattern becomes visible, and it stops looking like something wrong with you. You were
        responding, correctly, to something real.
      </P>

      <P>
        The exhaustion of walking on eggshells is the cost of bracing for a pattern you were
        never allowed to name. Seeing it laid out is often the moment you can finally set it
        down. This is{" "}
        <InlineLink to="/articulation-before-adjudication">
          the emerging category these tools belong to →
        </InlineLink>
      </P>

      <CTAButton label="Analyze a conversation →" />

      <p className="mt-10 text-[13px] leading-[1.6] text-white/40 text-center max-w-[560px] mx-auto">
        If you ever feel unsafe or in crisis, you don't have to handle it alone. In the U.S.,
        you can call or text <span className="text-white/55">988</span> (Suicide &amp; Crisis
        Lifeline) or contact the National Domestic Violence Hotline at{" "}
        <span className="text-white/55">1-800-799-7233</span>, available 24/7.
      </p>

      <H2>Further reading</H2>
      <ul className="mt-4 space-y-3 list-none pl-0">
        <li>
          <Link
            to="/why-do-i-feel-exhausted-after-conversations"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            Why do I feel exhausted after conversations →
          </Link>
        </li>
        <li>
          <Link
            to="/why-do-i-look-like-the-crazy-one"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            Why do I look like the crazy one →
          </Link>
        </li>
        <li>
          <Link
            to="/emotional-invalidation-examples"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            Emotional invalidation examples →
          </Link>
        </li>
        <li>
          <Link
            to="/articulation-before-adjudication"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            Articulation before adjudication →
          </Link>
        </li>
      </ul>
    </SeoPageShell>
  );
};

export default WhyWalkingOnEggshells;
